import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from 'lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--rely-radius-control)] text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rely-color-brand-green)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--rely-surface-page)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'min-h-[var(--rely-control-height-default)] rounded-[var(--rely-radius-control)] bg-[var(--rely-color-brand-green)] text-[var(--rely-color-brand-black)] shadow-[var(--rely-shadow-panel)] hover:bg-[var(--rely-color-brand-green-hover)] focus-visible:ring-[var(--rely-color-brand-green)]',
        default:
          'bg-secondary-500 text-white shadow-md hover:bg-secondary-600 focus-visible:ring-secondary-500',
        destructive:
          'bg-error-500 text-white shadow-md hover:bg-error-600 focus-visible:ring-error-500',
        outline:
          'border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] text-[var(--rely-text-strong)] hover:bg-[var(--rely-surface-elevated)]',
        secondary:
          'border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] text-[var(--rely-text-strong)] hover:bg-[var(--rely-surface-elevated)]',
        ghost:
          'text-[var(--rely-text-muted)] hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)]',
        link: 'text-secondary-500 underline-offset-4 hover:underline dark:text-secondary-400',
        success:
          'bg-primary-500 text-white shadow-md hover:bg-primary-600 focus-visible:ring-primary-500',
        icon:
          'min-h-[var(--rely-control-height-default)] rounded-[var(--rely-radius-control)] bg-[var(--rely-surface-panel)] text-[var(--rely-text-strong)] hover:bg-[var(--rely-surface-elevated)] focus-visible:ring-[var(--rely-color-brand-green)]',
      },
      size: {
        default: 'h-11 px-5 py-2',
        sm: 'h-9 px-4 text-xs',
        lg: 'h-12 px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
